import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Button,
  Collapse,
  Modal,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  CodeOutlined,
  ReloadOutlined,
  SaveOutlined,
  WarningOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

const PROMPT_HEADING_PATTERNS = [
  /^analysis json:?$/i,
  /^source json:?$/i,
  /^context json:?$/i,
  /^primary task:?$/i,
  /^output type:?$/i,
  /^core rules:?$/i,
  /^creative philosophy:?$/i,
  /^mandatory psychology filter:?$/i,
  /^visual execution rules:?$/i,
  /^copy rules:?$/i,
  /^quality control:?$/i,
  /^internal response contract:?$/i,
  /^json rules:?$/i,
  /^reference examples:?$/i,
  /^user feedback \(condensed\):?$/i,
  /^user preferences \(condensed\):?$/i,
  /^general knowledge \(key points only\):?$/i,
  /^copy library \(condensed\):?$/i,
  /^script constraints:?$/i,
  /^editing constraints:?$/i,
  /^environment context:?$/i,
  /^action context:?$/i,
  /^script context:?$/i,
  /^shortened script target.*:?$/i,
  /^actor profile.*:?$/i,
  /^continuity locks.*:?$/i,
  /^prompt channel:?$/i,
  /^stitch intent:?$/i,
  /^realism notes:?$/i,
  /^style direction:?$/i,
  /^user prompt:?$/i,
  /^brand:?$/i,
  /^product:?$/i,
  /^scene & style:?$/i,
  /^hard rules.*:?$/i,
  /^tag directives:?$/i,
  /^special instructions:?$/i,
  /^scene preference:?$/i,
  /^compliance \/ framing:?$/i,
  /^product fidelity.*:?$/i,
  /^source-ad fidelity:?$/i,
  /^knowledge-base source of truth:?$/i,
];

const isDividerLine = (line) => /^[=\-_*#]{3,}\s*$/.test(String(line || "").trim());

const normalizeSectionLabel = (line) =>
  String(line || "")
    .replace(/^##\s+/, "")
    .replace(/:\s*$/, "")
    .trim() || "Prompt";

const isPromptHeading = (line) => {
  const trimmed = String(line || "").trim();
  if (!trimmed) return false;
  if (/^##\s+/.test(trimmed)) return true;
  if (PROMPT_HEADING_PATTERNS.some((pattern) => pattern.test(trimmed))) {
    return true;
  }
  if (
    trimmed.length <= 64 &&
    /^[A-Z0-9][A-Z0-9 /&()+_-]{2,}$/.test(trimmed) &&
    /[A-Z]/.test(trimmed)
  ) {
    return true;
  }
  return (
    trimmed.length <= 56 &&
    /^[A-Z][A-Za-z0-9 /&()+_-]{2,}:\s*$/.test(trimmed)
  );
};

const splitPromptIntoSections = (prompt) => {
  const normalized = String(prompt || "").replace(/\r\n?/g, "\n");
  if (!normalized.trim()) return [];
  const lines = normalized.split("\n");
  const sections = [];
  let current = { label: "Prompt", lines: [] };

  const flushCurrent = () => {
    const text = current.lines.join("\n").trim();
    if (!text) return;
    sections.push({
      label: current.label || "Prompt",
      text,
    });
  };

  const startSection = (label, initialLines = []) => {
    flushCurrent();
    current = {
      label: normalizeSectionLabel(label),
      lines: [...initialLines],
    };
  };

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const trimmed = String(line || "").trim();
    const previous = lines[index - 1];
    const next = lines[index + 1];
    const isDividerHeading =
      trimmed &&
      isDividerLine(previous) &&
      isDividerLine(next) &&
      trimmed.length <= 80;

    if (isDividerHeading) {
      if (
        current.lines.length > 0 &&
        String(current.lines[current.lines.length - 1] || "") ===
          String(previous || "")
      ) {
        current.lines.pop();
      }
      startSection(trimmed, [String(previous || ""), line, String(next || "")]);
      index += 1;
      continue;
    }

    if (isPromptHeading(trimmed)) {
      startSection(trimmed, [line]);
      continue;
    }

    current.lines.push(line);
  }

  flushCurrent();
  return sections.length > 0
    ? sections
    : [{ label: "Prompt", text: normalized.trim() }];
};

const PromptSectionsView = ({ prompt, isModified }) => {
  const sections = useMemo(() => splitPromptIntoSections(prompt), [prompt]);

  if (!String(prompt || "").trim()) {
    return <Text type="secondary">No prompt available yet.</Text>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <Text type="secondary" style={{ fontSize: 12 }}>
        This is the raw text that will be sent to the model.
        {isModified
          ? " The preview reflects the current override."
          : " The preview reflects the default live prompt."}
      </Text>
      {sections.map((section, index) => (
        <div
          key={`${section.label}-${index}`}
          style={{
            borderLeft: "2px solid rgba(128, 128, 128, 0.2)",
            paddingLeft: 10,
          }}
        >
          <Text
            style={{
              fontSize: 11,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              opacity: 0.55,
              display: "block",
              marginBottom: 4,
            }}
          >
            {section.label}
          </Text>
          <pre
            style={{
              margin: 0,
              fontSize: 12,
              lineHeight: 1.55,
              fontFamily: "monospace",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              opacity: 0.88,
            }}
          >
            {section.text}
          </pre>
        </div>
      ))}
    </div>
  );
};

/**
 * PromptEditorModal — lets users view and edit the AI prompts for any section.
 *
 * Props:
 *   open         boolean
 *   onClose      () => void
 *   title        string   — modal heading (e.g. "Ad Copy Prompts")
 *   prompts      Array<{ id: string, label: string, value: string, description?: string }>
 *                — the "live" (built) prompt values from the current run context
 *   overrides    Record<string, string>   — current saved overrides (empty string = no override)
 *   onSave       (overrides: Record<string, string>) => void
 */
const PromptEditorModal = ({
  open,
  onClose,
  title = "Edit Prompts",
  prompts = [],
  overrides = {},
  onSave,
}) => {
  const [drafts, setDrafts] = useState({});

  // Keep a ref to the latest prompts/overrides so the open-gate effect always
  // reads the freshest values without re-running when they change mid-session.
  const promptsRef = useRef(prompts);
  const overridesRef = useRef(overrides);
  useEffect(() => { promptsRef.current = prompts; });
  useEffect(() => { overridesRef.current = overrides; });

  // Only initialise drafts when the modal transitions to open — never while
  // the user is actively editing (which would overwrite their changes).
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (!open) { wasOpenRef.current = false; return; }
    if (wasOpenRef.current) return; // already initialised for this open session
    wasOpenRef.current = true;
    const initial = {};
    for (const p of promptsRef.current) {
      const ov = overridesRef.current[p.id];
      initial[p.id] = ov !== undefined && ov !== "" ? ov : p.value ?? "";
    }
    setDrafts(initial);
  }, [open]);

  const hasAnyOverride = prompts.some(
    (p) => (drafts[p.id] ?? "") !== (p.value ?? ""),
  );

  const handleReset = (id) => {
    const original = prompts.find((p) => p.id === id)?.value ?? "";
    setDrafts((prev) => ({ ...prev, [id]: original }));
  };

  const handleResetAll = () => {
    const reset = {};
    for (const p of prompts) reset[p.id] = p.value ?? "";
    setDrafts(reset);
  };

  const handleSave = () => {
    const result = {};
    for (const p of prompts) {
      const draft = drafts[p.id] ?? "";
      // Only store if user actually changed it from the live value
      result[p.id] = draft !== (p.value ?? "") ? draft : "";
    }
    onSave?.(result);
    onClose?.();
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <Space>
          <CodeOutlined />
          <span>{title}</span>
          {hasAnyOverride && (
            <Tag color="orange" icon={<WarningOutlined />} style={{ marginLeft: 4 }}>
              Modified
            </Tag>
          )}
        </Space>
      }
      width={820}
      footer={
        <Space style={{ width: "100%", justifyContent: "space-between", display: "flex" }}>
          <Button icon={<ReloadOutlined />} onClick={handleResetAll} size="small">
            Reset All to Default
          </Button>
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" icon={<SaveOutlined />} onClick={handleSave}>
              Save Prompts
            </Button>
          </Space>
        </Space>
      }
      styles={{ body: { maxHeight: "70vh", overflowY: "auto", padding: "16px 24px" } }}
    >
      {prompts.length === 0 ? (
        <Text type="secondary">No prompts available yet. Run a generation first to preview prompts.</Text>
      ) : (
        <Space direction="vertical" size={20} style={{ width: "100%" }}>
          {prompts.map((p, idx) => {
            const draft = drafts[p.id] ?? "";
            const isModified = draft !== (p.value ?? "");
            const sectionCount = splitPromptIntoSections(draft).length || 1;
            return (
              <div key={p.id}>
                <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 6, display: "flex" }}>
                  <Space size={6}>
                    <Text strong style={{ fontSize: 13 }}>
                      {prompts.length > 1 ? `Prompt ${idx + 1}` : "Prompt"}
                      {p.label ? ` — ${p.label}` : ""}
                    </Text>
                    {isModified && <Tag color="orange" style={{ fontSize: 11 }}>Modified</Tag>}
                  </Space>
                  <Tooltip title="Reset this prompt to its default">
                    <Button
                      size="small"
                      icon={<ReloadOutlined />}
                      onClick={() => handleReset(p.id)}
                      disabled={!isModified}
                    >
                      Reset
                    </Button>
                  </Tooltip>
                </Space>
                {p.description && (
                  <Text type="secondary" style={{ display: "block", fontSize: 12, marginBottom: 6 }}>
                    {p.description}
                  </Text>
                )}
                <Collapse
                  size="small"
                  defaultActiveKey={["preview"]}
                  style={{ marginBottom: 10 }}
                  items={[
                    {
                      key: "preview",
                      label: (
                        <Space size={8} wrap>
                          <span>Raw Text Sent To Model</span>
                          <Tag color="blue">
                            {sectionCount} section
                            {sectionCount === 1 ? "" : "s"}
                          </Tag>
                          <Tag color={isModified ? "orange" : "default"}>
                            {isModified ? "Override Preview" : "Default Preview"}
                          </Tag>
                        </Space>
                      ),
                      children: (
                        <PromptSectionsView
                          prompt={draft}
                          isModified={isModified}
                        />
                      ),
                    },
                  ]}
                />
                <textarea
                  value={draft}
                  onChange={(e) => setDrafts((prev) => ({ ...prev, [p.id]: e.target.value }))}
                  style={{
                    width: "100%",
                    minHeight: prompts.length === 1 ? 280 : 220,
                    fontFamily: "monospace",
                    fontSize: 12,
                    lineHeight: 1.5,
                    padding: "8px 10px",
                    borderRadius: 6,
                    border: isModified ? "1.5px solid #fa8c16" : "1px solid #d9d9d9",
                    background: isModified ? "#fffbe6" : "#fafafa",
                    resize: "vertical",
                    boxSizing: "border-box",
                    outline: "none",
                  }}
                />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {draft.length.toLocaleString()} characters
                  {isModified ? " · Override active — this text will be sent instead of the default" : " · Default (unmodified)"}
                </Text>
              </div>
            );
          })}
        </Space>
      )}
    </Modal>
  );
};

/**
 * Small trigger button — shows a badge dot when overrides are active.
 * Use this next to any generate button.
 */
export const PromptEditorButton = ({ onClick, hasOverride = false, size = "small", label = "Prompts" }) => (
  <Badge dot={hasOverride} color="orange" offset={[-2, 2]}>
    <Tooltip title={hasOverride ? `${label} modified — click to view/edit` : `View & edit AI prompts: ${label}`}>
      <Button
        size={size}
        icon={<CodeOutlined />}
        onClick={onClick}
        style={hasOverride ? { borderColor: "#fa8c16", color: "#fa8c16" } : {}}
      >
        {label}
      </Button>
    </Tooltip>
  </Badge>
);

export default PromptEditorModal;
